import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { ChevronUp, ChevronDown } from "lucide-react";

interface ScrollNavigationProps {
  className?: string;
  showThreshold?: number; // Show buttons after scrolling this many pixels
}

export function ScrollNavigation({ 
  className = "", 
  showThreshold = 200 
}: ScrollNavigationProps) {
  const [isVisible, setIsVisible] = useState(false);
  const [canScrollUp, setCanScrollUp] = useState(false);
  const [canScrollDown, setCanScrollDown] = useState(true);

  const checkScrollPosition = () => {
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const scrollHeight = document.documentElement.scrollHeight;
    const clientHeight = document.documentElement.clientHeight;
    
    // Show/hide based on scroll position
    setIsVisible(scrollTop > showThreshold);
    
    // Check if we can scroll up or down
    setCanScrollUp(scrollTop > 0);
    setCanScrollDown(scrollTop < scrollHeight - clientHeight - 10);
  };

  useEffect(() => {
    checkScrollPosition();
    window.addEventListener('scroll', checkScrollPosition);
    window.addEventListener('resize', checkScrollPosition);
    
    return () => {
      window.removeEventListener('scroll', checkScrollPosition);
      window.removeEventListener('resize', checkScrollPosition);
    };
  }, [showThreshold]);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  const scrollToBottom = () => {
    window.scrollTo({
      top: document.documentElement.scrollHeight,
      behavior: 'smooth'
    });
  };

  const scrollUp = () => {
    const currentScroll = window.pageYOffset || document.documentElement.scrollTop;
    window.scrollTo({
      top: Math.max(0, currentScroll - window.innerHeight * 0.8),
      behavior: 'smooth'
    });
  };

  const scrollDown = () => {
    const currentScroll = window.pageYOffset || document.documentElement.scrollTop;
    window.scrollTo({
      top: currentScroll + window.innerHeight * 0.8,
      behavior: 'smooth'
    });
  };

  if (!isVisible) return null;

  return (
    <div className={`fixed right-6 top-1/2 -translate-y-1/2 z-50 flex flex-col gap-2 ${className}`} data-testid="scroll-navigation">
      {canScrollUp && (
        <Button
          variant="outline"
          size="sm"
          onClick={scrollUp}
          onDoubleClick={scrollToTop}
          className="w-10 h-10 p-0 bg-white/90 backdrop-blur-sm border-gray-300 shadow-lg hover:shadow-xl hover:scale-110 transition-all duration-200 hover:bg-purple-50 hover:border-purple-300 group"
          title="Scroll up (double-click for top)"
          data-testid="scroll-up-button"
        >
          <ChevronUp className="w-4 h-4 text-gray-600 group-hover:text-purple-600 transition-colors" />
        </Button>
      )}
      
      {canScrollDown && (
        <Button
          variant="outline"
          size="sm"
          onClick={scrollDown}
          onDoubleClick={scrollToBottom}
          className="w-10 h-10 p-0 bg-white/90 backdrop-blur-sm border-gray-300 shadow-lg hover:shadow-xl hover:scale-110 transition-all duration-200 hover:bg-purple-50 hover:border-purple-300 group"
          title="Scroll down (double-click for bottom)"
          data-testid="scroll-down-button"
        >
          <ChevronDown className="w-4 h-4 text-gray-600 group-hover:text-purple-600 transition-colors" />
        </Button>
      )}
    </div>
  );
}